import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  PASTE_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  $getRoot,
  type LexicalEditor,
} from 'lexical';
import { TGPE_CLIPBOARD_MIME } from './schema';
import { tryParseClipboardPayload } from './serialize';
import { extractTGPEHtmlComment } from './html-comment';
import type { TGPEClipboardPayload } from './schema';

export interface TGPEPastePluginProps {
  /**
   * When true, only apply TGPE payloads when the editor is empty so we
   * never clobber a draft. Default true.
   */
  onlyWhenEmpty?: boolean;
  /** Fired after a TGPE payload is successfully applied. */
  onPayloadParsed?: (payload: TGPEClipboardPayload) => void;
  /**
   * Fired when a payload was parseable but its schema version differs from
   * what this build understands.
   */
  onSchemaVersionMismatch?: (got: string, expected: string) => void;
}

/**
 * Lexical plugin that intercepts PASTE_COMMAND when the clipboard's
 * synchronous DataTransfer carries a TGPE payload, and applies it to the
 * editor. For all other pastes (HTML, plain text), this plugin returns
 * false and lets the rest of the plugin chain handle it normally — most
 * importantly PasteFilterPlugin which sanitizes external HTML.
 *
 * NB: Chromium-based engines do not always expose `web `-prefixed custom
 * MIME types via DataTransfer.getData() — they require the async
 * navigator.clipboard.read() API. The fully reliable round-trip path is
 * pasteFromTGPEClipboard() (an explicit action, e.g. bound to a toolbar
 * button or shortcut). This plugin is best-effort for environments that
 * do expose the type synchronously.
 */
export function TGPEPastePlugin({
  onlyWhenEmpty = true,
  onPayloadParsed,
  onSchemaVersionMismatch,
}: TGPEPastePluginProps = {}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent | InputEvent) => {
        if (!(event instanceof ClipboardEvent)) return false;
        const raw = readSyncTGPEPayload(event);
        if (!raw) return false;

        const result = tryParseClipboardPayload(raw);
        if (!result.payload) {
          if (result.versionMismatch) {
            onSchemaVersionMismatch?.(
              result.versionMismatch.got,
              result.versionMismatch.expected,
            );
          }
          return false;
        }

        if (onlyWhenEmpty && !isEditorEmpty(editor)) return false;

        event.preventDefault();
        editor.setEditorState(editor.parseEditorState(result.payload.state));
        onPayloadParsed?.(result.payload);
        return true;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, onlyWhenEmpty, onPayloadParsed, onSchemaVersionMismatch]);

  return null;
}

export interface PasteFromTGPEClipboardOptions {
  editor: LexicalEditor;
  onlyWhenEmpty?: boolean;
  onPayloadParsed?: (payload: TGPEClipboardPayload) => void;
  onSchemaVersionMismatch?: (got: string, expected: string) => void;
  /** Called when the clipboard had no TGPE payload — UI may want to toast. */
  onMiss?: () => void;
}

/**
 * Imperatively read the clipboard, find the TGPE payload, and apply it.
 * Bind this to an explicit user action (toolbar button or Cmd+Shift+V),
 * because reading the clipboard is a privileged async operation that
 * needs a user gesture. Resolves true if a payload was applied.
 */
export async function pasteFromTGPEClipboard(
  opts: PasteFromTGPEClipboardOptions,
): Promise<boolean> {
  const {
    editor,
    onlyWhenEmpty = true,
    onPayloadParsed,
    onSchemaVersionMismatch,
    onMiss,
  } = opts;

  const raw = await readTGPEFromClipboard();
  if (raw === null) {
    onMiss?.();
    return false;
  }

  const result = tryParseClipboardPayload(raw);
  if (!result.payload) {
    if (result.versionMismatch) {
      onSchemaVersionMismatch?.(
        result.versionMismatch.got,
        result.versionMismatch.expected,
      );
    } else {
      onMiss?.();
    }
    return false;
  }

  if (onlyWhenEmpty && !isEditorEmpty(editor)) return false;

  editor.setEditorState(editor.parseEditorState(result.payload.state));
  onPayloadParsed?.(result.payload);
  return true;
}

function isEditorEmpty(editor: LexicalEditor): boolean {
  return editor.getEditorState().read(() => $getRoot().getTextContentSize() === 0);
}

/**
 * Try to find a TGPE payload in the synchronous DataTransfer. We check the
 * custom MIME first (cheapest, future-proof) and then fall back to looking
 * for a `<!--TGPE:...-->` comment in the text/html flavor — that comment
 * is what makes the round-trip work in WebKit-based webviews where the
 * `web ` MIME is dropped on read.
 */
function readSyncTGPEPayload(event: ClipboardEvent): string | null {
  const direct = event.clipboardData?.getData(TGPE_CLIPBOARD_MIME);
  if (direct) return direct;
  const html = event.clipboardData?.getData('text/html');
  if (!html) return null;
  return extractTGPEHtmlComment(html);
}

async function readTGPEFromClipboard(): Promise<string | null> {
  try {
    const items = await navigator.clipboard.read();
    // eslint-disable-next-line no-console
    console.log('[TGPE paste] clipboard items:', items.length, items.map((i) => i.types));
    for (const item of items) {
      if (item.types.includes(TGPE_CLIPBOARD_MIME)) {
        const blob = await item.getType(TGPE_CLIPBOARD_MIME);
        return await blob.text();
      }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[TGPE paste] clipboard.read failed:', err);
  }
  return null;
}
